import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { ArrowRightSquare, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { format, isSameDay, parseISO, startOfMonth, endOfMonth, addMonths, addDays, startOfWeek, endOfWeek } from 'date-fns';
import MiniMonthDayDetail from './MiniMonthDayDetail';
import { getCalendarBookings, CalendarBooking } from '../../lib/bookings';
import { getCalendarFieldQuotes } from '../../lib/fieldQuotes/calendarFieldQuotes';
import { supabase } from '../../lib/supabase';
import { getCalendarTasks } from '../../lib/tasks';
import { getResources } from '../../lib/resources';
import { isGcalDigitalEvent, isGcalInPersonEvent, truncateGcalLocation } from '../../lib/gcalEventFormat';

interface GhostBooking {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

interface MiniMonthViewProps {
  businessId: string;
  currentMonth: Date;
  onMonthChange: (newMonth: Date) => void;
  onDayClick?: (date: Date) => void;
  /** expand = inline day detail under the week row (control centre); navigate = call onDayClick */
  dayClickBehavior?: 'navigate' | 'expand';
  expandedDay?: Date | null;
  onExpandedDayChange?: (day: Date | null) => void;
  onBookingClick?: (bookingId: string) => void;
  /** When set with expanded day, shows blue + to create booking (CalendarV2 month-day flow). */
  onDayAddClick?: (day: Date) => void;
  showDayAddButton?: boolean;
  selectedDate?: string;
  ghostBooking?: GhostBooking | null;
  /** When true, scrolls the month grid so the week containing today is in view (aligned to bottom). */
  scrollToCurrentWeek?: boolean;
  /** Opens full calendar month view (control centre). */
  onNavigateToMonthView?: () => void;
  /** iOS-style chrome for control centre panels (matches CalendarTodaySchedule). */
  iosStyled?: boolean;
}

const IOS_FONT =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif";

const EVENT_HEIGHT = 15;
const EVENT_GAP = 2;
const DAY_HEADER_HEIGHT = 20;

function getContrastText(hex: string): string {
  if (!hex || hex.length < 7) return '#ffffff';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1f2937' : '#ffffff';
}

function toDayStart(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

interface PlacedEvent {
  booking: CalendarBooking;
  startCol: number;
  endCol: number;
  lane: number;
  isMultiDay: boolean;
  isStart: boolean;
  isEnd: boolean;
}

function placeEventsForWeek(bookings: CalendarBooking[], weekDays: Date[]): PlacedEvent[] {
  const weekStartTs = toDayStart(weekDays[0]);
  const weekEndTs = toDayStart(weekDays[6]) + 24 * 60 * 60000;

  const relevant = bookings
    .filter(b => {
      if (b.extendedProps?.is_buffer_block) return false;
      try {
        const bStart = parseISO(b.start);
        const bEnd = parseISO(b.end);
        return bStart.getTime() < weekEndTs && bEnd.getTime() > weekStartTs;
      } catch { return false; }
    })
    .sort((a, b) => {
      try {
        const aStart = parseISO(a.start).getTime();
        const bStart = parseISO(b.start).getTime();
        if (aStart !== bStart) return aStart - bStart;
        const aDur = parseISO(a.end).getTime() - aStart;
        const bDur = parseISO(b.end).getTime() - bStart;
        return bDur - aDur;
      } catch { return 0; }
    });

  const lanes: { endCol: number; bookingId: string }[][] = [];
  const placed: PlacedEvent[] = [];

  for (const booking of relevant) {
    try {
      const bStart = parseISO(booking.start);
      const bEnd = parseISO(booking.end);
      const bStartDay = toDayStart(bStart);
      const bEndDay = toDayStart(bEnd);
      const isMultiDay = bEndDay > bStartDay;

      const clampedStartTs = Math.max(bStartDay, weekStartTs);
      const clampedEndTs = Math.min(
        isMultiDay ? bEnd.getTime() : bEndDay + 24 * 60 * 60000,
        weekEndTs
      );

      const startCol = Math.max(0, Math.floor((clampedStartTs - weekStartTs) / (24 * 60 * 60000)));
      let endCol: number;
      if (isMultiDay) {
        endCol = Math.min(6, Math.ceil((clampedEndTs - weekStartTs) / (24 * 60 * 60000)) - 1);
      } else {
        endCol = startCol;
      }

      if (endCol < startCol) endCol = startCol;

      const isStart = bStartDay >= weekStartTs;
      const isEnd = bEnd.getTime() <= weekEndTs;

      let assignedLane = -1;
      for (let l = 0; l < lanes.length; l++) {
        const conflict = lanes[l].some(e => !(endCol < e.endCol - (e.endCol - startCol) || startCol > e.endCol));
        const simpleConflict = lanes[l].some(e => {
          const eStart = placed.find(p => p.booking.id === e.bookingId);
          if (!eStart) return false;
          return !(endCol < eStart.startCol || startCol > e.endCol);
        });
        if (!simpleConflict) {
          assignedLane = l;
          break;
        }
      }
      if (assignedLane === -1) {
        assignedLane = lanes.length;
        lanes.push([]);
      }

      lanes[assignedLane].push({ endCol, bookingId: booking.id });
      placed.push({ booking, startCol, endCol, lane: assignedLane, isMultiDay, isStart, isEnd });
    } catch {
      // skip malformed events
    }
  }

  return placed;
}

export default function MiniMonthView({
  businessId,
  currentMonth,
  onMonthChange,
  onDayClick,
  dayClickBehavior = 'navigate',
  expandedDay: expandedDayProp,
  onExpandedDayChange,
  onBookingClick,
  onDayAddClick,
  showDayAddButton = false,
  selectedDate,
  ghostBooking,
  scrollToCurrentWeek = false,
  onNavigateToMonthView,
  iosStyled = false,
}: MiniMonthViewProps) {
  const [internalExpandedDay, setInternalExpandedDay] = useState<Date | null>(null);
  const expandedDay = expandedDayProp !== undefined ? expandedDayProp : internalExpandedDay;
  const setExpandedDay = (day: Date | null) => {
    if (onExpandedDayChange) onExpandedDayChange(day);
    else setInternalExpandedDay(day);
  };

  const [allBookings, setAllBookings] = useState<CalendarBooking[]>([]);
  const [resourceById, setResourceById] = useState<
    Record<string, { id: string; name: string; avatarUrl: string | null; initials: string }>
  >({});
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const weekRowRefs = useRef<(HTMLDivElement | null)[]>([]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days: Date[] = [];
  let cursor = gridStart;
  while (cursor <= gridEnd) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!businessId || !supabase) return;
      setLoading(true);
      try {
        const fetchStart = gridStart;
        const fetchEnd = addDays(gridEnd, 1);
        const fmtStart = format(fetchStart, 'yyyy-MM-dd');
        const fmtEnd = format(fetchEnd, 'yyyy-MM-dd');

        const [
          mobileBookings,
          locationBookings,
          timeOffResult,
          fieldQuoteEvents,
          bufferResult,
          gcalResult,
          taskEvents,
          resourcesList,
        ] = await Promise.all([
          getCalendarBookings(businessId, fetchStart, fetchEnd, false),
          getCalendarBookings(businessId, fetchStart, fetchEnd, true),
          supabase
            .from('time_off_requests')
            .select(`
              id,
              start_date,
              end_date,
              request_type,
              is_partial_day,
              start_time,
              end_time,
              resource_id,
              resources:resource_id ( id, first_name, last_name )
            `)
            .eq('business_id', businessId)
            .eq('status', 'approved')
            .lte('start_date', fmtEnd)
            .gte('end_date', fmtStart),
          getCalendarFieldQuotes(businessId, fetchStart, fetchEnd),
          supabase
            .from('mobile_bookings')
            .select('id, resource_id, asset_id, booking_id, start_date, start_time, end_date, end_time, buffer_time, is_buffer_block')
            .eq('business_id', businessId)
            .eq('is_buffer_block', true)
            .lte('start_date', fmtEnd)
            .gte('end_date', fmtStart),
          supabase
            .from('google_calendar_events')
            .select('id, title, start_time, end_time, all_day, resource_id, description, location, meet_link, conference_data')
            .eq('business_id', businessId)
            .eq('is_deleted', false)
            .gte('start_time', fetchStart.toISOString())
            .lte('start_time', fetchEnd.toISOString()),
          getCalendarTasks(businessId, fetchStart, fetchEnd),
          getResources(businessId),
        ]);

        if (cancelled) return;

        const byId: Record<
          string,
          { id: string; name: string; avatarUrl: string | null; initials: string }
        > = {};
        for (const r of resourcesList || []) {
          const name = `${r.first_name || ''} ${r.last_name || ''}`.trim();
          if (!r.id || !name) continue;
          byId[r.id] = {
            id: r.id,
            name,
            avatarUrl: r.avatar_url ?? null,
            initials: name
              .split(/\s+/)
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2),
          };
        }
        setResourceById(byId);

        const timeOffEvents: CalendarBooking[] = (timeOffResult.data || []).map((req: any) => {
          let endDateStr = req.end_date;
          if (!req.is_partial_day) {
            const [y, m, d] = req.end_date.split('-').map(Number);
            endDateStr = format(addDays(new Date(y, m - 1, d), 1), 'yyyy-MM-dd');
          }
          const resource = Array.isArray(req.resources) ? req.resources[0] : req.resources;
          const resourceName = resource
            ? `${resource.first_name || ''} ${resource.last_name || ''}`.trim()
            : byId[req.resource_id]?.name || '';
          return {
            id: `timeoff_${req.id}`,
            title: 'Time Off',
            booking_number: 0,
            start: req.is_partial_day && req.start_time ? `${req.start_date}T${req.start_time}:00` : req.start_date,
            end: req.is_partial_day && req.end_time ? `${req.end_date}T${req.end_time}:00` : endDateStr,
            backgroundColor: '#000000',
            borderColor: '#000000',
            allDay: !req.is_partial_day,
            display: 'block',
            resourceId: req.resource_id,
            extendedProps: {
              is_time_off: true,
              status: 'Time Off',
              customer: resourceName,
              booking_number: 0,
              subtotal: 0,
              total_amount: 0,
            },
          } as CalendarBooking;
        });

        const bufferEvents: CalendarBooking[] = (bufferResult.data || []).map((block: any) => {
          const sObj = new Date(`${block.start_date}T${block.start_time}`);
          const eObj = new Date(`${block.end_date}T${block.end_time}`);
          return {
            id: `buffer-${block.id}`,
            title: 'Buffer',
            booking_number: 0,
            start: sObj.toISOString(),
            end: eObj.toISOString(),
            backgroundColor: '#000000',
            borderColor: '#000000',
            allDay: false,
            display: 'block',
            resourceId: block.resource_id || block.asset_id,
            extendedProps: { is_buffer_block: true, status: 'buffer', customer: '', booking_number: 0, subtotal: 0, total_amount: 0 },
          } as CalendarBooking;
        });

        const gcalBookings: CalendarBooking[] = (gcalResult.data || []).map((ge: any) => ({
          id: `gcal_${ge.id}`,
          title: ge.title || 'Google Event',
          booking_number: 0,
          start: ge.start_time,
          end: ge.end_time,
          backgroundColor: '#4285f4',
          borderColor: '#4285f4',
          allDay: ge.all_day,
          display: 'block',
          resourceId: ge.resource_id,
          extendedProps: {
            is_gcal: true,
            status: 'gcal',
            customer: '',
            booking_number: 0,
            subtotal: 0,
            total_amount: 0,
            is_gcal_digital: isGcalDigitalEvent(ge.meet_link, ge.conference_data),
            is_gcal_in_person: isGcalInPersonEvent(ge.location),
            gcal_location: ge.location?.trim() || null,
          },
        } as CalendarBooking));

        setAllBookings([
          ...mobileBookings, ...locationBookings,
          ...timeOffEvents, ...fieldQuoteEvents,
          ...bufferEvents, ...gcalBookings, ...taskEvents,
        ]);
      } catch (err) {
        console.error('MiniMonthView: failed to load data', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [businessId, currentMonth, reloadToken]);

  useEffect(() => {
    const refresh = () => setReloadToken((t) => t + 1);
    const events = [
      'bookingCreated',
      'mobileBookingCreated',
      'taskCreated',
      'controlCentreCalendarRefresh',
    ] as const;
    events.forEach((name) => window.addEventListener(name, refresh));
    return () => events.forEach((name) => window.removeEventListener(name, refresh));
  }, []);

  const GHOST_ID = '__ghost_booking__';

  const bookingsWithGhost = useMemo(() => {
    if (!ghostBooking || !ghostBooking.startDate || !ghostBooking.startTime || !ghostBooking.endDate || !ghostBooking.endTime) {
      return allBookings;
    }
    const ghostEntry: CalendarBooking = {
      id: GHOST_ID,
      title: 'New Booking',
      booking_number: 0,
      start: `${ghostBooking.startDate}T${ghostBooking.startTime}`,
      end: `${ghostBooking.endDate}T${ghostBooking.endTime}`,
      backgroundColor: '__ghost__',
      borderColor: '#3b82f6',
      allDay: false,
      display: 'block',
      resourceId: '',
      extendedProps: { customer: '', booking_number: 0, subtotal: 0, total_amount: 0, status: 'ghost' },
    } as CalendarBooking;
    return [...allBookings, ghostEntry];
  }, [allBookings, ghostBooking]);

  const weekPlacements = useMemo(() => {
    return weeks.map(week => placeEventsForWeek(bookingsWithGhost, week));
  }, [bookingsWithGhost, weeks]);

  const today = new Date();

  const currentWeekIndex = useMemo(() => {
    const now = new Date();
    return weeks.findIndex((week) => week.some((day) => isSameDay(day, now)));
  }, [weeks]);

  useLayoutEffect(() => {
    if (!scrollToCurrentWeek || loading) return;

    const scrollToWeek = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      if (currentWeekIndex < 0) {
        container.scrollTop = 0;
        return;
      }

      const weekEl = weekRowRefs.current[currentWeekIndex];
      if (!weekEl) return;

      const containerRect = container.getBoundingClientRect();
      const weekRect = weekEl.getBoundingClientRect();
      const scrollDelta = weekRect.bottom - containerRect.bottom;
      container.scrollTop = Math.max(0, container.scrollTop + scrollDelta);
    };

    scrollToWeek();
    const raf = requestAnimationFrame(scrollToWeek);
    return () => cancelAnimationFrame(raf);
  }, [scrollToCurrentWeek, loading, currentMonth, currentWeekIndex, weekPlacements]);

  const getEventLabel = (booking: CalendarBooking): string => {
    const customer = booking.extendedProps?.customer;
    if (customer && typeof customer === 'string' && customer.trim()) return customer.trim();
    if (booking.title) return booking.title;
    return 'Booking';
  };

  const getEventStartTime = (booking: CalendarBooking): string => {
    try {
      const dt = parseISO(booking.start);
      return format(dt, 'h:mma').toLowerCase();
    } catch { return ''; }
  };

  const getEventEndTime = (booking: CalendarBooking): string => {
    try {
      const dt = parseISO(booking.end);
      return format(dt, 'h:mma').toLowerCase();
    } catch { return ''; }
  };

  const getGcalFormatSuffix = (extendedProps: CalendarBooking['extendedProps']): string => {
    const props = extendedProps as {
      is_gcal_digital?: boolean;
      is_gcal_in_person?: boolean;
      gcal_location?: string | null;
    } | undefined;
    const parts: string[] = [];
    if (props?.is_gcal_digital) parts.push('Digital');
    if (props?.is_gcal_in_person) {
      parts.push(
        props.gcal_location
          ? truncateGcalLocation(props.gcal_location, 14)
          : 'In person'
      );
    }
    return parts.length ? ` · ${parts.join(' · ')}` : '';
  };

  const dayHeaderH = iosStyled ? 18 : DAY_HEADER_HEIGHT;
  const eventH = iosStyled ? 12 : EVENT_HEIGHT;
  const eventGap = iosStyled ? 1 : EVENT_GAP;

  const iosNavButtonClass =
    'flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg bg-white border border-black/[0.08] text-gray-700 shadow-sm active:bg-gray-100 transition-colors touch-manipulation shrink-0';

  const handleDayClick = (day: Date) => {
    if (dayClickBehavior === 'expand') {
      const next = expandedDay && isSameDay(expandedDay, day) ? null : day;
      setExpandedDay(next);
      return;
    }
    onDayClick?.(day);
  };

  const expandedWeekIndex = useMemo(() => {
    if (!expandedDay) return -1;
    return weeks.findIndex((week) => week.some((d) => isSameDay(d, expandedDay)));
  }, [expandedDay, weeks]);

  useLayoutEffect(() => {
    if (dayClickBehavior !== 'expand' || !expandedDay || expandedWeekIndex < 0) return;
    const container = scrollContainerRef.current;
    const weekEl = weekRowRefs.current[expandedWeekIndex];
    if (!container || !weekEl) return;

    const containerRect = container.getBoundingClientRect();
    const weekRect = weekEl.getBoundingClientRect();
    if (weekRect.bottom > containerRect.bottom || weekRect.top < containerRect.top) {
      weekEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [dayClickBehavior, expandedDay, expandedWeekIndex, currentMonth]);

  return (
    <div
      className={`flex flex-col h-full ${iosStyled ? 'min-h-0' : ''}`}
      style={iosStyled ? { fontFamily: IOS_FONT } : undefined}
    >
      {iosStyled ? (
        <div
          className="flex items-center justify-between shrink-0 border-b border-black/[0.06] bg-white px-2.5 py-2 sm:px-3 sm:py-2.5"
          data-cc-inset-header
        >
          <div className="min-w-0 flex-1">
            <h3 className="text-[13px] sm:text-[15px] font-semibold text-gray-900 truncate tracking-tight">
              <span className="sm:hidden">{format(currentMonth, 'MMMM yyyy')}</span>
              <span className="hidden sm:inline">Month</span>
            </h3>
            <p className="hidden sm:block text-[13px] text-gray-500 mt-0.5">
              {format(currentMonth, 'MMMM yyyy')}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-1.5 sm:gap-1.5 sm:ml-2">
            <button
              type="button"
              onClick={() => onMonthChange(addMonths(currentMonth, -1))}
              className={iosNavButtonClass}
              aria-label="Previous month"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => onMonthChange(addMonths(currentMonth, 1))}
              className={iosNavButtonClass}
              aria-label="Next month"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={2} />
            </button>
            {onNavigateToMonthView ? (
              <button
                type="button"
                onClick={onNavigateToMonthView}
                className={iosNavButtonClass}
                aria-label="Open calendar month view"
                title="Open month view"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ArrowRightSquare className="h-4 w-4" strokeWidth={2} />
              </button>
            ) : null}
          </div>
        </div>
      ) : (
      <div className="flex items-center justify-between gap-1 px-3 py-2 border-b border-gray-200 flex-shrink-0">
        <button
          type="button"
          onClick={() => onMonthChange(addMonths(currentMonth, -1))}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4 text-gray-600" />
        </button>
        <span className="text-sm font-semibold text-gray-800 truncate flex-1 text-center min-w-0">
          {format(currentMonth, 'MMMM yyyy')}
        </span>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => onMonthChange(addMonths(currentMonth, 1))}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
          {onNavigateToMonthView ? (
            <button
              type="button"
              onClick={onNavigateToMonthView}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-black/[0.08] bg-white text-gray-700 shadow-sm active:bg-gray-100 transition-colors touch-manipulation"
              aria-label="Open calendar month view"
              title="Open month view"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <ArrowRightSquare className="h-4 w-4" strokeWidth={2} />
            </button>
          ) : null}
        </div>
      </div>
      )}

      {loading && (
        <div
          className={`flex flex-1 items-center justify-center ${iosStyled ? 'bg-[var(--cc-surface,#f8f9fb)]' : 'py-12'}`}
        >
          <Loader2 className={`animate-spin text-gray-400 ${iosStyled ? 'h-6 w-6' : 'h-5 w-5'}`} />
        </div>
      )}

      {!loading && (
        <div
          ref={scrollContainerRef}
          className={`flex-1 overflow-y-auto min-h-0 ios-momentum-scroll ${iosStyled ? 'bg-[var(--cc-surface,#f8f9fb)] px-0.5 pb-1 sm:px-1' : ''}`}
        >
          <div
            className={`grid grid-cols-7 sticky top-0 z-[1] ${
              iosStyled
                ? 'border-b border-black/[0.06] bg-[var(--cc-surface,#f8f9fb)]'
                : 'border-b border-gray-200 bg-white'
            }`}
          >
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div
                key={d}
                className={
                  iosStyled
                ? 'text-center text-[10px] sm:text-[11px] font-medium text-gray-400 py-1.5 sm:py-2 tracking-wide'
                : 'text-center text-[10px] font-bold text-gray-500 py-1.5 border-r border-gray-100 last:border-r-0'
                }
              >
                {iosStyled ? d.charAt(0) : d}
              </div>
            ))}
          </div>

          {weeks.map((week, wIdx) => {
            const placed = weekPlacements[wIdx];
            const maxLane = placed.length > 0 ? Math.max(...placed.map(p => p.lane)) : -1;
            const totalLanes = maxLane + 1;
            const eventAreaHeight = totalLanes * (eventH + eventGap);
            const gridHeight = dayHeaderH + eventAreaHeight + (iosStyled ? 2 : 4);
            const showDayDetail =
              dayClickBehavior === 'expand' &&
              expandedDay &&
              week.some((d) => isSameDay(d, expandedDay));

            return (
              <div
                key={wIdx}
                ref={(el) => {
                  weekRowRefs.current[wIdx] = el;
                }}
                className={
                  iosStyled
                    ? 'relative mx-0.5 mb-1 rounded-lg sm:mx-1 sm:mb-1 sm:rounded-xl bg-white border border-black/[0.04] shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden'
                    : 'relative border-b border-gray-100 last:border-b-0'
                }
              >
                <div className="relative" style={{ height: gridHeight }}>
                <div className="grid grid-cols-7 absolute inset-0">
                  {week.map((day, dIdx) => {
                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                    const isToday = isSameDay(day, today);
                    const isExpandedDay =
                      dayClickBehavior === 'expand' &&
                      expandedDay &&
                      isSameDay(day, expandedDay);
                    const isSelected =
                      isExpandedDay ||
                      (selectedDate && isSameDay(day, new Date(selectedDate + 'T00:00:00')));

                    return (
                      <div
                        key={dIdx}
                        className={
                          iosStyled
                            ? `cursor-pointer transition-colors active:bg-black/[0.04] ${
                                isSelected ? 'bg-blue-500/10' : ''
                              }`
                            : `border-r border-gray-100 last:border-r-0 cursor-pointer transition-colors ${
                                !isCurrentMonth ? 'bg-gray-50/60' : 'hover:bg-blue-50/20'
                              } ${isSelected ? 'bg-blue-50/50 ring-1 ring-inset ring-blue-400' : ''}`
                        }
                        onClick={() => handleDayClick(day)}
                      >
                        <div
                          className={`flex ${iosStyled ? 'justify-center pt-1 sm:pt-1.5' : 'justify-end px-1 pt-1'}`}
                        >
                          <span
                            className={
                              iosStyled
                                ? `text-[11px] sm:text-[12px] font-medium leading-none ${
                                    !isCurrentMonth
                                      ? 'text-gray-300'
                                      : isToday
                                        ? 'bg-[#FF3B30] text-white rounded-full min-w-[20px] h-[20px] sm:min-w-[22px] sm:h-[22px] flex items-center justify-center text-[10px] sm:text-[11px] font-semibold'
                                        : 'text-gray-900'
                                  }`
                                : `text-[11px] font-semibold leading-none ${
                                    !isCurrentMonth
                                      ? 'text-gray-300'
                                      : isToday
                                        ? 'bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]'
                                        : 'text-gray-700'
                                  }`
                            }
                          >
                            {day.getDate()}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Event bars — absolutely positioned to span columns */}
                {placed.map(p => {
                    const { booking, startCol, endCol, lane, isMultiDay, isStart, isEnd } = p;
                    const isGhost = booking.id === GHOST_ID;
                    const color = isGhost ? 'transparent' : (booking.backgroundColor || '#3b82f6');
                    const textColor = isGhost ? '#3b82f6' : getContrastText(color);
                    const isTimeOff = booking.extendedProps?.is_time_off;
                    const isGcal = (booking.extendedProps as any)?.is_gcal;
                    const gcalFormatSuffix = isGcal ? getGcalFormatSuffix(booking.extendedProps) : '';
                    const label = getEventLabel(booking);
                    const sTime = getEventStartTime(booking);
                    const eTime = getEventEndTime(booking);

                    const leftPct = (startCol / 7) * 100;
                    const widthPct = ((endCol - startCol + 1) / 7) * 100;
                    const topPx = dayHeaderH + lane * (eventH + eventGap);

                    const borderRadius = isMultiDay
                      ? `${isStart ? '3px' : '0'} ${isEnd ? '3px' : '0'} ${isEnd ? '3px' : '0'} ${isStart ? '3px' : '0'}`
                      : '3px';

                    return (
                      <div
                        key={`${booking.id}-w${wIdx}`}
                        className="absolute pointer-events-none flex items-center"
                        style={{
                          left: `calc(${leftPct}% + ${iosStyled ? 1 : 2}px)`,
                          width: `calc(${widthPct}% - ${iosStyled ? 2 : 4}px)`,
                          top: topPx,
                          height: eventH,
                          backgroundColor: isGhost
                            ? 'rgba(59, 130, 246, 0.12)'
                            : isGcal ? 'rgba(66, 133, 244, 0.15)' : color,
                          color: isGhost ? '#3b82f6' : isGcal ? '#1d4ed8' : textColor,
                          borderRadius,
                          border: isGhost ? '2px dashed rgba(59, 130, 246, 0.5)' : 'none',
                          borderLeft: !isGhost ? (isGcal ? '2px solid #4285f4' : isTimeOff ? '2px solid rgba(255,255,255,0.3)' : 'none') : undefined,
                          fontSize: iosStyled ? '8px' : '9px',
                          lineHeight: `${eventH}px`,
                          paddingLeft: iosStyled ? 2 : 3,
                          paddingRight: iosStyled ? 2 : 3,
                          opacity: isTimeOff ? 0.85 : 1,
                          zIndex: isGhost ? 8 : 5,
                          overflow: 'hidden',
                        }}
                        title={
                          isGhost
                            ? 'New Booking'
                            : `${label}${sTime ? ` — ${sTime}` : ''}${eTime ? ` to ${eTime}` : ''}${gcalFormatSuffix}${isMultiDay ? ' (multi-day)' : ''}`
                        }
                      >
                        {isGhost && !isMultiDay && (
                          <span className="truncate font-semibold">
                            {sTime ? sTime : ''}{sTime && eTime ? ' – ' : ''}{eTime ? eTime : ''}{!sTime && ' New Booking'}
                          </span>
                        )}
                        {isGhost && isMultiDay && (
                          <>
                            {isStart && (
                              <span className="font-semibold flex-shrink-0 whitespace-nowrap">
                                {sTime || 'New Booking'}
                              </span>
                            )}
                            <span className="flex-1 mx-1 border-b border-dashed flex-shrink-0" style={{ borderColor: 'rgba(59, 130, 246, 0.4)', minWidth: 4 }} />
                            {isEnd && (
                              <span className="font-semibold flex-shrink-0 whitespace-nowrap">
                                {eTime || ''}
                              </span>
                            )}
                          </>
                        )}
                        {!isGhost && !isMultiDay && (
                          <span className="truncate font-semibold">
                            {sTime && !isTimeOff ? sTime : ''}{sTime && eTime && !isTimeOff ? ' – ' : ''}{eTime && !isTimeOff ? eTime : ''}
                            {isGcal && gcalFormatSuffix}
                            {isTimeOff && label}
                          </span>
                        )}
                        {!isGhost && isMultiDay && (
                          <>
                            {isStart && (
                              <span className="font-semibold flex-shrink-0 whitespace-nowrap truncate">
                                {sTime && !isTimeOff ? sTime : label}
                                {isGcal && isStart && gcalFormatSuffix}
                              </span>
                            )}
                            <span className="flex-1 mx-1 border-b border-dashed flex-shrink-0" style={{ borderColor: textColor, opacity: 0.4, minWidth: 4 }} />
                            {isEnd && (
                              <span className="font-semibold flex-shrink-0 whitespace-nowrap">
                                {eTime && !isTimeOff ? eTime : label}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {showDayDetail && expandedDay ? (
                  <MiniMonthDayDetail
                    day={expandedDay}
                    bookings={bookingsWithGhost}
                    resourceById={resourceById}
                    iosStyled={iosStyled}
                    onBookingClick={onBookingClick}
                    showAddButton={showDayAddButton}
                    onAddClick={
                      onDayAddClick ? () => onDayAddClick(expandedDay) : undefined
                    }
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
